import { createRoute } from "@tanstack/react-router";
import { useState } from "react";

import { ProtectedRoute } from "../auth/ProtectedRoute";
import { rootRoute } from "./__root";
import { useTemplates } from "../queries";
import { TemplateEditor } from "../components/cockpit/templates/TemplateEditor";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import type { TemplateListItem } from "../queries";

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/templates",
  component: TemplatesRoute,
});

function TemplatesRoute() {
  return (
    <ProtectedRoute requiredRoles={["owner", "gm"]}>
      <TemplatesScreen />
    </ProtectedRoute>
  );
}

function TemplatesScreen() {
  const templates = useTemplates();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const items = templates.data?.items || [];
  const requestedTemplateCode = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("template")
    : null;
  const requestedTemplate = requestedTemplateCode
    ? items.find((item) => item.code === requestedTemplateCode)
    : null;
  const speedToLeadTemplate = items.find((item) => item.code === "speed_to_lead_booking_sms_v1") || null;
  const postQuoteSteps = getPostQuoteSequence(items);
  const postQuoteIds = new Set(postQuoteSteps.map((step) => step.template.id));
  const otherTemplates = items
    .filter((item) => !postQuoteIds.has(item.id) && item.id !== speedToLeadTemplate?.id)
    .sort((a, b) => templateSortKey(a).localeCompare(templateSortKey(b)));
  const effectiveSelected = selectedId || requestedTemplate?.id || postQuoteSteps[0]?.template.id || speedToLeadTemplate?.id || otherTemplates[0]?.id || null;

  return (
    <section className="space-y-5">
      <div>
        <Badge variant="slate">Settings</Badge>
        <h1 className="mt-3 text-2xl font-semibold text-slate-950">Templates</h1>
        <p className="mt-1 text-sm text-slate-600">Edit the active messages used by the customer follow-up agent. The post-quote sequence is shown in the order customers receive it.</p>
      </div>
      <div className="grid gap-5 xl:grid-cols-[380px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Customer Sequence</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-600">
              Active post-quote cadence: T+3h, J+2, J+4, J+6, J+8, J+11, J+14, J+16. Sunday sends are deferred to Monday, and the sequence stops when the customer replies or the quote closes.
            </div>

            <div>
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Post-quote follow-up</div>
              <div className="space-y-2">
                {postQuoteSteps.map((step) => (
                  <TemplateButton
                    key={step.template.id}
                    item={step.template}
                    selected={effectiveSelected === step.template.id}
                    eyebrow={`${step.when} · ${step.template.channel.toUpperCase()}`}
                    title={step.title}
                    description={step.description}
                    onClick={() => setSelectedId(step.template.id)}
                  />
                ))}
                {!postQuoteSteps.length ? <div className="text-xs text-slate-500">No active post-quote templates.</div> : null}
              </div>
            </div>

            {speedToLeadTemplate ? (
              <div>
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Speed to Lead</div>
                <TemplateButton
                  item={speedToLeadTemplate}
                  selected={effectiveSelected === speedToLeadTemplate.id}
                  eyebrow="SMS"
                  title="Booking SMS"
                  description="Tenant-specific WebFindYou booking message. Must keep booking link, callback phone, and STOP language."
                  onClick={() => setSelectedId(speedToLeadTemplate.id)}
                />
              </div>
            ) : null}

            {otherTemplates.length ? (
              <div>
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Other active templates</div>
                <div className="space-y-2">
                  {otherTemplates.map((item) => (
                    <TemplateButton
                      key={item.id}
                      item={item}
                      selected={effectiveSelected === item.id}
                      eyebrow={item.channel.toUpperCase()}
                      title={humanTemplateTitle(item)}
                      description={item.purpose || item.name}
                      onClick={() => setSelectedId(item.id)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
        <TemplateEditor templateId={effectiveSelected} />
      </div>
    </section>
  );
}

type PostQuoteStep = {
  code: string;
  when: string;
  title: string;
  description: string;
};

const postQuoteSequence: PostQuoteStep[] = [
  {
    code: "relance_sms_t3h_v2",
    when: "T+3h",
    title: "Quote received check",
    description: "First SMS after the estimate, with STOP language.",
  },
  {
    code: "relance_email_j2_v2",
    when: "J+2",
    title: "Warm clarity check",
    description: "Email to confirm the quote is clear and invite questions.",
  },
  {
    code: "relance_sms_j4_v2",
    when: "J+4",
    title: "SMS clarity check",
    description: "Short SMS asking if one part needs a walkthrough.",
  },
  {
    code: "relance_email_j6_v2",
    when: "J+6",
    title: "Before/after context",
    description: "Email with gallery context to make the scope easier to judge.",
  },
  {
    code: "relance_sms_j8_v2",
    when: "J+8",
    title: "Before/after offer",
    description: "SMS asking if pictures would help.",
  },
  {
    code: "relance_email_j11_v2",
    when: "J+11",
    title: "Scheduling window",
    description: "Email asking whether timing is still useful.",
  },
  {
    code: "relance_sms_j14_v2",
    when: "J+14",
    title: "Keep open or close",
    description: "Final SMS permission check.",
  },
  {
    code: "relance_email_j16_v2",
    when: "J+16",
    title: "Final file cleanup",
    description: "Final email asking whether to hold or move toward next step.",
  },
];

function getPostQuoteSequence(items: TemplateListItem[]) {
  const byCode = new Map(items.map((item) => [item.code, item]));
  return postQuoteSequence
    .map((step) => {
      const template = byCode.get(step.code);
      return template ? { ...step, template } : null;
    })
    .filter((step): step is PostQuoteStep & { template: TemplateListItem } => Boolean(step));
}

function templateSortKey(item: TemplateListItem) {
  return `${item.channel}-${humanTemplateTitle(item)}-${item.code || item.id}`;
}

function humanTemplateTitle(item: TemplateListItem) {
  if (item.code === "speed_to_lead_booking_sms_v1") return "Speed to Lead booking SMS";
  if (item.code?.startsWith("relance_first_touch_older")) return "Older quote reactivation";
  if (item.code?.startsWith("review_")) return "Review campaign";
  if (item.code?.startsWith("referral_")) return "Referral campaign";
  if (item.code?.startsWith("care_guide")) return "Care guide";
  if (item.code?.startsWith("accepted_reply")) return "Internal alert";
  return item.name;
}

function TemplateButton({
  item,
  selected,
  eyebrow,
  title,
  description,
  onClick,
}: {
  item: TemplateListItem;
  selected: boolean;
  eyebrow: string;
  title: string;
  description?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`w-full rounded-md border p-3 text-left text-sm transition ${selected ? "border-slate-900 bg-slate-50" : "border-slate-100 hover:bg-slate-50"}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{eyebrow}</div>
          <div className="mt-1 font-medium text-slate-950">{title}</div>
        </div>
        <span className="rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-500">{item.channel}</span>
      </div>
      {description ? <div className="mt-1 text-xs leading-5 text-slate-500">{description}</div> : null}
      <div className="mt-2 font-mono text-[11px] text-slate-400">{item.code || `template_${item.id}`}</div>
    </button>
  );
}
